'use client'

import { type ElementType, type ReactNode, useEffect, useRef } from 'react'
import { cn } from './cn'

/**
 * A heading whose lines rise out of a blur as the section comes into view, with one
 * `*highlighted*` phrase in the brand colour that can cycle through other phrases once
 * the reveal has played.
 *
 * Ported from the WyseAi site. Built on GSAP's SplitText (bundled with `gsap`, which the
 * product already depends on): it measures the line boxes the browser laid out, so the
 * lines that rise are the lines on screen. `aria: 'auto'` keeps the heading one sentence
 * for a screen reader. `prefers-reduced-motion` is honoured by `gsap.matchMedia`, which
 * never splits or animates for anyone who asked for less motion.
 *
 * The server renders the finished heading as plain text: with no JavaScript it is simply
 * a heading. The highlight is a `<span>`, never `<em>`: the emphasis is colour, not slant.
 * SplitText is told to `ignore` it, and it is `nowrap`, so a phrase never straddles a
 * line break.
 */
const HIGHLIGHT_SELECTOR = '.reveal-glue, .text-brand, .text-brand-on-ink'
const HIGHLIGHT = /\*([^*]+)\*/
const DURATION_S = 0.9
const STAGGER_S = 0.12
const EASE_NAME = 'rise-blur'
const TRIGGER_START = 'top 88%'
const CYCLE_HOLD_S = 1.8
const CYCLE_OUT_S = 0.3

/**
 * Punctuation that follows a highlight directly ("*Visible usage*.") is glued to it in a
 * `nowrap` wrapper. An `inline-block` is an atomic inline, and a line may break on either
 * side of one, so without the glue a heading that just fits its line sets the full stop
 * on a line of its own whenever SplitText is not running: under reduced motion, and with
 * no JavaScript. SplitText ignores the wrapper too, carrying phrase and punctuation as one.
 */
const GLUED_PUNCTUATION = /^[.,;:!?…)]+/

function withHighlight(text: string, className: string): ReactNode {
  const parts = text.split(HIGHLIGHT)
  if (parts.length === 1) return text
  const nodes: ReactNode[] = []
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index] ?? ''
    if (index % 2 === 0) {
      nodes.push(part)
      continue
    }
    // Odd parts are the captured phrases; one per heading, so the phrase is its key.
    const following = parts[index + 1] ?? ''
    const glued = GLUED_PUNCTUATION.exec(following)?.[0] ?? ''
    parts[index + 1] = following.slice(glued.length)
    const highlight = (
      <span
        className={cn('inline-block whitespace-nowrap', className)}
        data-highlight=""
        key={part}
      >
        {part}
      </span>
    )
    nodes.push(
      glued ? (
        <span className="reveal-glue whitespace-nowrap" key={`${part}-glue`}>
          {highlight}
          {glued}
        </span>
      ) : (
        highlight
      ),
    )
  }
  return nodes
}

/**
 * Cycle the highlight: the outgoing phrase fades, the incoming one rises out of a blur
 * from below with the reveal's own values. One fresh timeline per swap, because a
 * repeating timeline re-rendered the incoming tween's hidden start state on every wind
 * back and the phrase sat invisible through each hold.
 */
function startCycle(
  gsap: typeof import('gsap').gsap,
  node: HTMLElement,
  phrases: readonly string[] | undefined,
): { kill: () => void } | undefined {
  if (!phrases || phrases.length < 2) return undefined
  const span = node.querySelector<HTMLElement>('[data-highlight]')
  if (!span) return undefined

  let index = Math.max(0, phrases.indexOf(span.textContent ?? ''))
  let stopped = false
  let current: { kill: () => void } | undefined

  const swap = () => {
    if (stopped) return
    current = gsap
      .timeline({ delay: CYCLE_HOLD_S, onComplete: swap })
      .to(span, { opacity: 0, duration: CYCLE_OUT_S, ease: 'power2.in' })
      .call(() => {
        index = (index + 1) % phrases.length
        span.textContent = phrases[index] ?? ''
      })
      .fromTo(
        span,
        { opacity: 0, y: 30, filter: 'blur(10px)' },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: DURATION_S,
          ease: EASE_NAME,
          immediateRender: false,
          clearProps: 'filter',
        },
      )
  }
  swap()

  return {
    kill: () => {
      stopped = true
      current?.kill()
      gsap.set(span, { clearProps: 'opacity,transform,filter' })
    },
  }
}

export function RevealText({
  text,
  as: Tag = 'h2',
  className,
  highlightClassName = 'text-brand',
  cycle,
}: {
  readonly text: string
  readonly as?: ElementType
  readonly className?: string
  /** The colour of the `*highlighted*` phrase; the dark band passes its on-ink shade. */
  readonly highlightClassName?: string
  /** Phrases the highlight cycles through once revealed; the first is the one in `text`. */
  readonly cycle?: readonly string[]
}) {
  const host = useRef<HTMLElement>(null)

  useEffect(() => {
    const node = host.current
    if (!node) return

    let cancelled = false
    let context: { revert: () => void } | undefined
    let cycling: { kill: () => void } | undefined

    // Loaded here, not at module scope, so GSAP stays out of the server render path and
    // the initial bundle.
    Promise.all([
      import('gsap'),
      import('gsap/SplitText'),
      import('gsap/ScrollTrigger'),
      import('gsap/CustomEase'),
    ]).then(([{ gsap }, { SplitText }, { ScrollTrigger }, { CustomEase }]) => {
      if (cancelled) return

      gsap.registerPlugin(SplitText, ScrollTrigger, CustomEase)
      if (!CustomEase.get(EASE_NAME))
        CustomEase.create(EASE_NAME, '0.22, 1, 0.36, 1')

      const media = gsap.matchMedia()
      media.add('(prefers-reduced-motion: no-preference)', () => {
        const split = SplitText.create(node, {
          type: 'lines,words',
          aria: 'auto',
          ignore: HIGHLIGHT_SELECTOR,
          reduceWhiteSpace: false,
          autoSplit: true,
          onSplit: (self) => {
            cycling?.kill()
            return gsap.from(self.lines, {
              opacity: 0,
              y: 30,
              filter: 'blur(10px)',
              duration: DURATION_S,
              stagger: STAGGER_S,
              ease: EASE_NAME,
              immediateRender: true,
              scrollTrigger: {
                trigger: node,
                start: TRIGGER_START,
                once: true,
              },
              clearProps: 'filter,willChange',
              onComplete: () => {
                cycling = startCycle(gsap, node, cycle)
              },
            })
          },
        })
        return () => {
          cycling?.kill()
          split.revert()
        }
      })
      context = media
    })

    return () => {
      cancelled = true
      context?.revert()
    }
  }, [cycle])

  return (
    <Tag className={className} data-reveal ref={host}>
      {withHighlight(text, highlightClassName)}
    </Tag>
  )
}
